// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Stack } from 'expo-router';
import { useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet } from 'react-native';

import { Text, View } from '@/components/Themed';
import { useUser } from '@/app/user-context';
import { useHoleFeatures, type HoleFeature } from '@/features/course-trace/useHoleFeatures';
import { useManageableCourses } from '@/hooks/useManageableCourses';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';

/**
 * Review of derived tee and green positions.
 *
 * Course mapping produces estimates, not facts: a position is an average over however many
 * players happened to walk past it, and the confidence score says how much to trust it.
 * Nothing derived is treated as authoritative until someone accepts it here, which is why
 * this screen exists rather than the pipeline publishing straight through.
 *
 * Rejecting deletes the estimate. Accepting also pins it, so a later recomputation refines
 * the position but cannot silently remove a feature a human has signed off.
 */
export default function CourseMappingReviewScreen() {
  const { t } = useI18n();
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  const { courses, loading: coursesLoading } = useManageableCourses(user?.token);
  const [courseId, setCourseId] = useState<string | null>(null);
  const { features, loading, review } = useHoleFeatures(apiBaseUrl, user?.token, courseId);
  const [busyId, setBusyId] = useState<string | null>(null);

  const holes = useMemo(() => {
    const byHole = new Map<number, HoleFeature[]>();
    for (const feature of features) {
      const list = byHole.get(feature.holeNumber) ?? [];
      list.push(feature);
      byHole.set(feature.holeNumber, list);
    }
    return [...byHole.entries()].sort((a, b) => a[0] - b[0]);
  }, [features]);

  const onReview = async (feature: HoleFeature, accepted: boolean) => {
    setBusyId(feature.id);
    try {
      await review(feature.id, accepted);
    } finally {
      setBusyId(null);
    }
  };

  const describe = (feature: HoleFeature) => {
    const kind = t(feature.kind === 'tee' ? 'courseMappingReview.tee' : 'courseMappingReview.green');
    // An OSM tee name ("Gul/Vit") identifies the box far better than an index does, so it
    // wins when present. Ordinal only means anything for tees anyway.
    if (feature.name) return `${kind} ${feature.name}`;
    return feature.kind === 'tee' && feature.ordinal > 0 ? `${kind} ${feature.ordinal + 1}` : kind;
  };

  return (
    <>
      <Stack.Screen options={{ title: t('courseMappingReview.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{t('courseMappingReview.title')}</Text>
        <Text style={styles.intro}>{t('courseMappingReview.intro')}</Text>

        <Text style={styles.heading}>{t('courseMappingReview.selectCourse')}</Text>
        {coursesLoading ? <ActivityIndicator /> : null}
        <View style={styles.courseList}>
          {courses.map((course) => (
            <Pressable
              key={course.id}
              testID={`course-option-${course.id}`}
              accessibilityRole="button"
              style={[styles.courseChip, courseId === course.id ? styles.courseChipActive : null]}
              onPress={() => setCourseId(course.id)}
            >
              <Text style={styles.courseChipText}>{course.name}</Text>
            </Pressable>
          ))}
        </View>

        {courseId === null ? null : loading ? (
          <ActivityIndicator />
        ) : holes.length === 0 ? (
          <Text style={styles.empty} testID="course-mapping-review-empty">
            {t('courseMappingReview.empty')}
          </Text>
        ) : (
          holes.map(([holeNumber, holeFeatures]) => (
            <View key={holeNumber} style={styles.holeSection}>
              <Text style={styles.holeHeading}>
                {t('courseMappingReview.hole', { number: holeNumber })}
              </Text>
              {holeFeatures.map((feature) => (
                <View key={feature.id} style={styles.featureRow} testID={`feature-${feature.id}`}>
                  <View style={styles.featureInfo}>
                    <Text style={styles.featureKind}>
                      {describe(feature)}
                      {feature.isReviewed ? ` · ${t('courseMappingReview.reviewed')}` : ''}
                    </Text>
                    <Text style={styles.featureMeta}>
                      {t('courseMappingReview.meta', {
                        confidence: Math.round(feature.confidence * 100),
                        contributors: feature.sampleCount,
                      })}
                    </Text>
                    <Text style={styles.featureCoords}>
                      {feature.lat.toFixed(6)}, {feature.lon.toFixed(6)}
                    </Text>
                  </View>
                  <View style={styles.actions}>
                    <Pressable
                      accessibilityRole="button"
                      testID={`accept-${feature.id}`}
                      disabled={busyId === feature.id || feature.isReviewed}
                      style={[styles.action, styles.accept]}
                      onPress={() => void onReview(feature, true)}
                    >
                      <Text style={styles.actionText}>{t('courseMappingReview.accept')}</Text>
                    </Pressable>
                    <Pressable
                      accessibilityRole="button"
                      testID={`reject-${feature.id}`}
                      disabled={busyId === feature.id}
                      style={[styles.action, styles.reject]}
                      onPress={() => void onReview(feature, false)}
                    >
                      <Text style={styles.actionText}>{t('courseMappingReview.reject')}</Text>
                    </Pressable>
                  </View>
                </View>
              ))}
            </View>
          ))
        )}
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 20,
    paddingBottom: 48,
    maxWidth: 720,
    alignSelf: 'center',
    width: '100%',
  },
  title: { fontSize: 26, fontWeight: '700' },
  intro: { fontSize: 16, lineHeight: 24, marginTop: 8, marginBottom: 20 },
  heading: { fontSize: 17, fontWeight: '600', marginBottom: 8 },
  courseList: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 20 },
  courseChip: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#88888855',
  },
  courseChipActive: { borderColor: '#4caf50', borderWidth: 2 },
  courseChipText: { fontSize: 14 },
  empty: { fontSize: 16, lineHeight: 24, opacity: 0.8 },
  holeSection: { marginBottom: 18 },
  holeHeading: { fontSize: 16, fontWeight: '600', marginBottom: 6 },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    gap: 12,
  },
  featureInfo: { flexShrink: 1 },
  featureKind: { fontSize: 15, fontWeight: '600' },
  featureMeta: { fontSize: 13, opacity: 0.75, marginTop: 2 },
  featureCoords: { fontSize: 12, opacity: 0.6, marginTop: 2 },
  actions: { flexDirection: 'row', gap: 8 },
  action: { paddingVertical: 6, paddingHorizontal: 12, borderRadius: 6 },
  accept: { backgroundColor: '#2e7d32' },
  reject: { backgroundColor: '#8d3b3b' },
  actionText: { color: '#ffffff', fontSize: 14, fontWeight: '600' },
});
